import type { Maybe } from '@adornis/base/utilTypes.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { type NarrowedEntity, selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { confirmDialog, guardDialog, promptDialog } from '@adornis/dialog/dialog-functions.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useMemo, useObservable } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { hoverTooltip } from '@adornis/popover/hover-tooltip.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { email } from '@adornis/validation/functions/email.js';
import { getLastEditor } from '@adornis/versioned-entity/api/get-last-editor.js';
import { computed } from '@lit-labs/preact-signals';
import { CardHeader } from '@adornis/components/client/CardHeader.js';
import { format } from 'date-fns';
import { html, nothing, type TemplateResult } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { of } from 'rxjs';
import { SgFormGridEntry } from '../../client/components/SgFormGridEntry.js';
import { Permission } from '../../permissions/roles.js';
import { Person } from '../entities/person.js';
import { UserListAPI } from '../list/api/UserListAPI.js';
import { InvitationOptionsForType, type InviteType } from '../spender-sorgeberechtigte/invitationOptions.js';
import { SpenderSorgeberechtigteAPI } from '../spender-sorgeberechtigte/SpenderSorgeberechtigteAPI.js';
import { SchuelerDaten } from './schueler-daten.js';
import { SgStudentInterviewData } from './sg-student-interview-data.js';

const loadingSkeleton = html`
  <div class="flex flex-col gap-4 m-4">
    <div class="card p-4">
      <div class="card-body gap-4">
        <div class="card-title skeleton h-12 flex grow w-full"></div>
        <div class="skeleton h-8 flex grow w-full"></div>
        <div class="skeleton h-8 flex grow w-full"></div>
        <div class="skeleton h-8 flex grow w-full"></div>
        <div class="skeleton h-8 flex grow w-full"></div>
        <div class="skeleton h-8 flex grow w-full"></div>
      </div>
    </div>

    ${repeat(
      Array.from({ length: 2 }),
      () => html`
        <div class="card p-4 flex flex-row items-center gap-4">
          <div class="skeleton h-12 flex grow"></div>
          <div class="skeleton h-12 flex grow"></div>
          <div class="skeleton h-12 flex grow"></div>
          <div class="skeleton h-12 flex grow"></div>
        </div>
      `,
    )}
  </div>
`;

const StudentDataForm = stateful(
  (props: {
    person: NarrowedEntity<
      Person,
      { _id: 1; externalUniqueID: 1; schuelerDaten: { heinNummer: 1; zuzugsDate: 1; participationReligion: 1 } }
    >;
  }) => {
    // the form edits a plain snapshot; `constructValue` turns it back into the entity right before saving
    const personData = useMemo(() => [props.person.toObject()], [props.person._id]);

    const form = useForm(personData, { validator: Person.prototype.validator.functions }, [personData]);

    const handleSave = async () => {
      const validationError = await form.validate();
      if (validationError) return;

      await guardDialog(
        async () => {
          await constructValue<Person>(form.document).save();
        },
        { successText: 'Schülerdaten erfolgreich gespeichert' },
      );
    };

    return html`
      <div class="card card-body">
        ${CardHeader({
          title: 'Systemdaten',
        })}
        <div class="grid grid-cols-[1fr_4fr] flex-auto gap-3">
          ${SgFormGridEntry({
            label: 'Hein-Nummer',
            render: TextInput({
              ...form.field('schuelerDaten', 'heinNummer'),
              placeholder: 'Hein-Nummer',
              placeholderMode: 'hide',
              clearable: true,
            }),
          })}
          ${SgFormGridEntry({
            label: 'Externe ID',
            render: TextInput({
              ...form.field('externalUniqueID'),
              placeholder: 'Externe ID',
              placeholderMode: 'hide',
              clearable: true,
            }),
          })}
        </div>
        <div class="flex flex-row justify-end">
          <button class="btn btn-primary btn-lg" @click=${handleSave}>
            <span class="icon-[material-symbols--save-outline-rounded] size-5"></span>
            Speichern
          </button>
        </div>
      </div>
    `;
  },
);

const InvitationCard = stateful(
  (props: {
    inviteType: InviteType;
    accepted: boolean;

    person:
      | string
      | NarrowedEntity<
          Person,
          { _id: 1; email: 1; debitorennummer: 1; profilDaten: { stammdaten: { vorname: 1; nachname: 1 } } }
        >;
    child?: NarrowedEntity<
      Person,
      { _id: 1; email: 1; schuelerDaten: { eingeladeneSorgeberechtigte: 1; eingeladeneSpender: 1 } }
    >;
  }) => {
    const user = useObservable(() => currentUser(), []);

    const options = InvitationOptionsForType[props.inviteType];

    const actions = computed(() => {
      if (!user) return [];

      const actions: TemplateResult[] = [];

      if (user.hasPermission(options.manualConfirmPermission) && !props.accepted) {
        actions.push(html`
          <button
            type="button"
            class="btn btn-square btn-ghost btn-sm tooltip hover:text-success"
            aria-label="Einladung manuell bestätigen"
            data-tip="Einladung manuell bestätigen"
            ${hoverTooltip()}
            @click=${async (e: Event) => {
              e.stopPropagation();
              e.preventDefault();

              await guardDialog(
                async () => {
                  if (!props.child?._id || typeof props.person !== 'string') return;
                  await SpenderSorgeberechtigteAPI.manualConfirm(props.inviteType, props.child._id, props.person)();
                },
                { success: () => XSnackbar.show('Verknüpfung erfolgreich bestätigt') },
              );
            }}
          >
            <span class="icon-[material-symbols--check-rounded] size-5"></span>
          </button>
        `);
      }

      if (user.hasPermission(Permission.TOGGLE_SPENDER_INVITE_TYPE) && props.accepted) {
        const otherInviteType = props.inviteType === 'Spender' ? 'Sorgeberechtigter' : 'Spender';
        actions.push(html`
          <button
            type="button"
            class="btn btn-square btn-ghost btn-sm tooltip hover:text-primary"
            aria-label="Zu ${otherInviteType} wechseln"
            ${hoverTooltip('top', `Zu ${otherInviteType} wechseln`)}
            @click=${async (e: Event) => {
              e.stopPropagation();
              e.preventDefault();

              await guardDialog(
                async () => {
                  if (!props.child?._id || typeof props.person === 'string') return;

                  await SpenderSorgeberechtigteAPI.toggleType(otherInviteType, props.child._id, props.person._id)();
                },
                { success: () => XSnackbar.show('Person wurde erfolgreich umgeschaltet') },
              );
            }}
          >
            <span class="icon-[material-symbols--swap-vert-rounded] size-5"></span>
          </button>
        `);
      }

      if (user.hasPermission(options.removePermission)) {
        if (props.accepted) {
          actions.push(html`
            <button
              type="button"
              class="btn btn-square btn-ghost btn-sm tooltip hover:text-error"
              aria-label="Verknüpfung löschen"
              data-tip="Verknüpfung löschen"
              ${hoverTooltip()}
              @click=${async (e: Event) => {
                e.stopPropagation();
                e.preventDefault();

                if (
                  !(await confirmDialog(
                    'Sind Sie sicher, dass Sie diese Verknüpfung löschen möchten? Die Person bleibt erhalten.',
                    { highlightDecline: true, acceptText: 'Löschen', declineText: 'Abbrechen' },
                  ))
                ) {
                  return;
                }

                await guardDialog(
                  async () => {
                    if (!props.child?._id || typeof props.person === 'string') return;
                    await SpenderSorgeberechtigteAPI.remove(props.inviteType, props.child._id, props.person._id)();
                  },
                  { success: () => XSnackbar.show('Verknüpfung erfolgreich gelöscht') },
                );
              }}
            >
              <span class="icon-[material-symbols--delete-rounded] size-5"></span>
            </button>
          `);
        } else {
          actions.push(html`
            <button
              type="button"
              class="btn btn-square btn-ghost btn-sm tooltip hover:text-error"
              aria-label="Einladung zurückziehen"
              data-tip="Einladung zurückziehen"
              ${hoverTooltip()}
              @click=${async (e: Event) => {
                e.stopPropagation();
                e.preventDefault();

                if (
                  !(await confirmDialog('Sind Sie sicher, dass Sie die Einladung zurückziehen möchten?', {
                    highlightDecline: true,
                    acceptText: 'Einladung zurückziehen',
                    declineText: 'Abbrechen',
                  }))
                ) {
                  return;
                }

                await guardDialog(
                  async () => {
                    if (!props.child?._id || typeof props.person !== 'string') return;
                    await SpenderSorgeberechtigteAPI.revoke(props.inviteType, props.child._id, props.person)();
                  },
                  { success: () => XSnackbar.show('Einladung erfolgreich zurückgezogen') },
                );
              }}
            >
              <span class="icon-[material-symbols--cancel-rounded] size-5"></span>
            </button>
          `);
        }
      }

      return actions;
    });

    return html`
      <a
        href=${
          props.accepted ? `/personen/${typeof props.person === 'string' ? props.person : props.person._id}` : nothing
        }
      >
        <div
          class="card gap-4 ${
            props.accepted
              ? 'bg-success/50 transition ease-in-out duration-150 border-transparent border-dashed border-4 hover:border-success cursor-pointer'
              : 'bg-warning/50 border-dashed border-4 border-warning'
          } p-6 flex flex-row items-center"
          href=${`/personen/${typeof props.person === 'string' ? props.person : props.person._id}`}
        >
          <p>
            <b>${typeof props.person === 'string' ? props.person : props.person.email}</b>
          </p>
          <p>
            ${typeof props.person === 'string' ? '' : props.person.profilDaten?.stammdaten?.vorname}
            ${typeof props.person === 'string' ? '' : props.person.profilDaten?.stammdaten?.nachname}
            (${typeof props.person === 'string' ? '' : props.person.debitorennummer})
          </p>
          ${
            actions.value.length
              ? html`
                  <div class="bg-base-100 h-6 flex-grow rounded-full p-4 flex items-center justify-center flex-row">
                    ${repeat(actions.value, action => action)}
                  </div>
                `
              : nothing
          }
        </div>
      </a>
    `;
  },
);

const InvitationManagement = stateful(
  (props: {
    inviteType: InviteType;
    person: NarrowedEntity<
      Person,
      {
        _id: 1;
        email: 1;
        schuelerDaten: { heinNummer: 1; eingeladeneSorgeberechtigte: 1; eingeladeneSpender: 1 };
      }
    >;
  }) => {
    const user = useObservable(() => currentUser(), []);

    const { value: acceptedPersons, loading: acceptedPersonLoading } = useQuery(
      () =>
        UserListAPI.subscribe(
          0,
          20,
          new AdornisFilter({
            aspects: [
              new StringAspect({
                fieldName: props.inviteType === 'Sorgeberechtigter' ? 'sorgeberechtigteKinderIDs' : 'spendenKinderIDs',
                mode: 'containsExactOR',
                value: props.person._id,
              }),
            ],
          }),
        )({ _id: 1, email: 1, debitorennummer: 1, profilDaten: { stammdaten: { vorname: 1, nachname: 1 } } }),
      [props.person._id],
    );

    const options = InvitationOptionsForType[props.inviteType];

    return html`
      <div class="card p-4 gap-4">
        <h2 class="card-title">${props.inviteType === 'Sorgeberechtigter' ? 'Sorgeberechtigte' : 'Spender'}</h2>
        <div class="flex flex-row gap-4 flex-wrap">
          ${
            acceptedPersonLoading
              ? html` <div class="skeleton h-12 flex grow"></div> `
              : html`${repeat(
                  acceptedPersons ?? [],
                  person => person._id,
                  person =>
                    html` ${InvitationCard({
                      inviteType: props.inviteType,
                      accepted: true,
                      person,
                      child: props.person,
                    })}`,
                )}
                ${repeat(
                  (
                    props.person.schuelerDaten?.[
                      props.inviteType === 'Sorgeberechtigter' ? 'eingeladeneSorgeberechtigte' : 'eingeladeneSpender'
                    ] ?? []
                  ).filter(email => !acceptedPersons?.some(person => person.email === email)),
                  email => email,
                  email =>
                    html` ${InvitationCard({
                      inviteType: props.inviteType,
                      accepted: false,
                      person: email,
                      child: props.person,
                    })}`,
                )}
                ${
                  user?.hasPermission(options.invitePermission)
                    ? html`
                        <button
                          type="button"
                          class="btn btn-dash btn-block justify-start"
                          @click=${async () => {
                            const userEmail = await promptDialog('Person einladen', {
                              placeholder: 'E-Mail-Adresse',
                              buttonText: 'Einladen',
                            }).catch(() => null);
                            if (!userEmail) return;

                            await guardDialog(
                              async () => {
                                await email()({ value: userEmail, key: 'E-Mail-Adresse', target: props.person });
                                await SpenderSorgeberechtigteAPI.invite(
                                  props.inviteType,
                                  props.person._id,
                                  userEmail,
                                )();
                              },
                              { success: () => XSnackbar.show('Person vorgemerkt – bitte manuell bestätigen') },
                            );
                          }}
                        >
                          <span class="icon-[material-symbols--add-rounded] size-8 text-success"></span>
                          <span>Person einladen</span>
                        </button>
                      `
                    : nothing
                } `
          }
        </div>
      </div>
    `;
  },
);

export const SgStudentDetails = stateful((props: { personID: Maybe<string> }) => {
  if (!props.personID) throw new Error('No user given');
  const user = useObservable(() => currentUser<Person>(), []);
  const { value: person, loading: personLoading } = useQuery(
    () =>
      subscribeByID(
        Person,
        props.personID,
      )({
        _id: 1,
        username: 1,
        email: 1,
        debitorennummer: 1,
        externalUniqueID: 1,
        roles: { name: 1, contexts: 1 },
        schuelerDaten: { ...selectionSet(() => SchuelerDaten, 1) },
      }),
    [props.personID],
  );

  const { value: lastEditorInfo, loading: lastEditorInfoLoading } = useQuery(
    () => (props.personID ? getLastEditor([props.personID])({ authorID: 1, timestamp: 1 }) : of(null)),
    [props.personID],
  );

  const { value: lastEditor, loading: lastEditorLoading } = useQuery(
    () => subscribeByID(Person, lastEditorInfo?.authorID)({ profilDaten: { stammdaten: { vorname: 1, nachname: 1 } } }),
    [lastEditorInfo?.authorID],
  );

  if (personLoading || lastEditorInfoLoading || lastEditorLoading || !person) return loadingSkeleton;

  // TODO no permission page
  if (
    !user?.hasPermission(Permission.SCHUELER_R) &&
    !user?.hasPermission(Permission.SCHUELER_BEWERBER_R) &&
    !user?.hasPermission(Permission.VIEW_SUS_INTERVIEW_DATA)
  )
    return html`<p>Keine Berechtigung</p>`;

  return html`
    <div class="flex flex-col gap-4 m-4">
      <!-- kept next to the new editor until everybody moved over; inviting by email exists only here for now -->
      <div role="alert" class="alert alert-warning alert-soft">
        <span class="icon-[material-symbols--warning-outline-rounded] size-6 shrink-0"></span>
        <span>
          Sorgeberechtigte und Spender werden jetzt im Profil unter <b>Beziehungen</b> verwaltet. Bitte nutzen Sie dort
          den neuen Editor – diese Ansicht wird bald entfernt. Nur das Einladen per E-Mail gibt es vorerst nur hier.
          <a class="link" href=${`/personen/${person._id}/profil`}>Zu den Beziehungen</a>
        </span>
      </div>
      ${InvitationManagement({ inviteType: 'Sorgeberechtigter', person })}
      ${InvitationManagement({ inviteType: 'Spender', person })} ${SgStudentInterviewData({ personID: props.personID })}
      ${StudentDataForm({ person })}
      <div class="h-64"></div>
    </div>

    ${
      user.hasPermission(Permission.SCHUELER_W)
        ? html`
            <div
              class="fixed bottom-0 z-20 flex items-center justify-between gap-4 p-4 border-t border-base-300 bg-base-100/80 shadow-[0_-8px_24px_-12px_rgb(16_42_49/0.18)] backdrop-blur-md backdrop-saturate-150"
              ${css({
                width: navigator.userAgent.toLowerCase().includes('firefox')
                  ? '-moz-available'
                  : '-webkit-fill-available',
              })}
            >
              <p>
                Zuletzt bearbeitet von
                <b>
                  ${lastEditor?.profilDaten?.stammdaten?.vorname} ${lastEditor?.profilDaten?.stammdaten?.nachname} am
                  ${lastEditorInfo?.timestamp ? format(lastEditorInfo.timestamp, "dd.MM.yyyy 'um' HH:mm") : '---'}
                </b>
              </p>
            </div>
          `
        : nothing
    }
  `;
});
